import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { rusDateOnly } from 'webui/utils/time';
import { Box, Button } from '@mui/material';
import { calendarSX, timeInputsSX } from './period-picker-styles';
import { useIMask } from 'react-imask';
import { any2unix } from 'webui/utils/unixtime';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { useBooleanState } from 'shared/hooks/state/boolean';
export function PeriodPicker(props) {
    const { onDatesSelected, onCancelClick } = props;
    const [calendarValue, setCalendarValue] = useState([new Date(), new Date()]);
    const [startTime, setStartTime] = useState('00:00');
    const [endTime, setEndTime] = useState('23:59');
    const [inputsError, setNoInputsError, setInputsError] = useBooleanState(false);
    const [enabled, setDisabled, setEnabled] = useBooleanState(true);
    const date2standart = (date) => {
        const result = date.split('.').reverse().join('-');
        return result;
    };
    const onStartDayComplete = useCallback((value) => {
        setCalendarValue((prev_value) => {
            if (Array.isArray(prev_value)) {
                const [, endDate] = prev_value;
                const newValue = new Date(date2standart(value));
                return [newValue, endDate];
            }
            return prev_value;
        });
    }, []);
    const { ref: startDayRef, value: startDayValue, setValue: setStartDayValue, } = useIMask({
        mask: Date,
        autofix: false,
        lazy: false,
        min: new Date(1999, 0, 1),
        max: new Date(),
    }, { onComplete: onStartDayComplete });
    const onEndDayComplete = useCallback((value) => {
        setCalendarValue((prev_value) => {
            if (Array.isArray(prev_value)) {
                const [startDate] = prev_value;
                const newValue = new Date(date2standart(value));
                return [startDate, newValue];
            }
            return prev_value;
        });
    }, []);
    const { ref: endDayRef, value: endDayValue, setValue: setEndDayValue, } = useIMask({
        mask: Date,
        autofix: false,
        lazy: false,
        min: new Date(1999, 0, 1),
        max: new Date(),
    }, { onComplete: onEndDayComplete });
    const validateInputs = (start, end) => {
        setNoInputsError();
        if (start.length < 10 || end.length < 10) {
            return false;
        }
        if (start.includes('_') || end.includes('_')) {
            return false;
        }
        const valid = any2unix(new Date(date2standart(end))) >=
            any2unix(new Date(date2standart(start)));
        if (!valid) {
            setInputsError();
        }
        return valid;
    };
    useEffect(() => {
        const valid = validateInputs(startDayValue, endDayValue);
        if (valid) {
            setEnabled();
        }
        else {
            setDisabled();
        }
    }, [startDayValue, endDayValue]);
    const handleCalendar = useCallback((value) => {
        if (Array.isArray(value)) {
            if (value.includes(null)) {
                const [startDate] = value;
                if (startDate !== null) {
                    setStartDayValue(rusDateOnly(startDate));
                    setEndDayValue(rusDateOnly(startDate));
                }
            }
            else {
                const [startDate, endDate] = value;
                if (startDate !== null && endDate !== null) {
                    setStartDayValue(rusDateOnly(startDate));
                    setEndDayValue(rusDateOnly(endDate));
                }
            }
        }
    }, []);
    const pickPeriod = useCallback(() => {
        if (Array.isArray(calendarValue)) {
            const [startDate, endDate] = calendarValue;
            if (startDate !== null && endDate !== null) {
                const [startTimeHours, startTimeMinutes] = startTime.split(':');
                startDate.setHours(Number(startTimeHours), Number(startTimeMinutes), 0);
                const [endTimeHours, endTimeMinutes] = endTime.split(':');
                endDate.setHours(Number(endTimeHours), Number(endTimeMinutes), 59);
                onDatesSelected(startDate, endDate);
                onCancelClick();
            }
        }
    }, [calendarValue, startTime, endTime]);
    return (_jsxs(_Fragment, { children: [_jsx(Box, { bgcolor: 'background.paper', color: 'text.secondary', sx: (theme) => calendarSX(theme), children: _jsx(Calendar, { onChange: handleCalendar, value: calendarValue, selectRange: true, allowPartialRange: true, showNeighboringMonth: false, minDetail: 'year', maxDate: new Date(), locale: 'ru-RU' }) }), _jsxs(Box, { bottom: '0', width: '100%', maxWidth: '350px', bgcolor: 'background.paper', p: 1.5, children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', rowGap: 1, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', columnGap: 3, children: [_jsx(TextInputWrapper, { inputProps: { ref: startDayRef }, error: inputsError, value: startDayValue || rusDateOnly(new Date()), onChange: setStartDayValue }), _jsx(TextInputWrapper, { inputProps: { ref: endDayRef }, error: inputsError, value: endDayValue || rusDateOnly(new Date()), onChange: setEndDayValue })] }), _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', columnGap: 3, sx: timeInputsSX, children: [_jsx(TextInputWrapper, { type: "time", onChange: (value) => setStartTime(value), value: startTime, fullWidth: true }), _jsx(TextInputWrapper, { type: "time", onChange: (value) => setEndTime(value), value: endTime, fullWidth: true })] })] }), _jsxs(Box, { mt: 1.5, display: 'flex', justifyContent: 'flex-end', columnGap: 2.5, className: "mui_calendar_buttons", children: [_jsx(Button, { onClick: onCancelClick, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { onClick: pickPeriod, disabled: !enabled, children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C" })] })] })] }));
}
